import { CommonActions, type NavigationState, type PartialState } from '@react-navigation/native'
import { useEvent } from '@shared-core/hooks/use-event'
import {
  buildExitFlowResetState,
  getExitCrossState,
  type NavigationLike,
  readExitFlowMeta,
  type ScreensList,
  useNavigation,
  useRoute,
} from '@shared-core/navigation'

type ExitCrossOptions = {
  /**
   * Куда выходить, если meta нет (диплинк / холодный вход). Нужен экранам без кнопки назад,
   * где крестик рендерится безусловно (`onCloseHandler={onExitFlow}`), иначе он станет no-op.
   */
  fallbackOrigin?: ScreensList
}

type ExitCrossResult = {
  /** Показывать крестик (hop >= 2; на flow-root — по входящей цепочке entry). */
  showExitCross: boolean
  /** Сброс стека к origin флоу. No-op, если origin неизвестен и fallbackOrigin не задан. */
  onExitFlow: () => void
  /** Origin из params (для отладки / редких кастомов). */
  origin: ScreensList | undefined
  hop: number
}

/**
 * Крестик выхода из многошагового флоу.
 * Meta `__exitFlow` инжектит навигационный слой (web/mobile adapters) — экран только читает.
 */
export function useExitCross(options?: ExitCrossOptions): ExitCrossResult {
  const route = useRoute<{ params?: unknown }>()
  const navigation = useNavigation<NavigationLike>()
  const meta = readExitFlowMeta(route.params)
  const cross = getExitCrossState(meta)
  const showExitCross = cross.isVisible
  const exitOrigin = cross.origin ?? options?.fallbackOrigin

  const onExitFlow = useEvent(() => {
    if (!exitOrigin) return

    // Таб-origin → AUTHORIZED/{tab}; Settings/Security/Paygine — sibling на корневом стеке.
    navigation.dispatch(CommonActions.reset(buildExitFlowResetState(exitOrigin) as PartialState<NavigationState>))
  })

  return { showExitCross, onExitFlow, origin: meta?.origin, hop: meta?.hop ?? 0 }
}
